import { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { applyStyles } from '../../utils/styleUtils';
import { resolvePlanPrices } from '../../utils/planPrices';
import { currencySign } from '../../utils/currency';
import StripeApplePay from './payment/StripeApplePay';
import StripeGooglePay from './payment/StripeGooglePay';

import { usePaymentStore, paymentStore, solidgateApplePayContainerRef, solidgateGooglePayContainerRef } from './payment/StripeHelper';

const SolidgateWalletSpinnerOverlay = () => (
    <div
        style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)',
            borderRadius: '4px',
            pointerEvents: 'none',
        }}
    >
        <span className="button-spinner" style={{ color: '#fff' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                    <animate attributeName="stroke-dasharray" values="1,150;90,150;1,150" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite" />
                </circle>
            </svg>
        </span>
    </div>
);

const PaywallItemsElement = ({ element }) => {
    const {
        data,
        getCurrentScreen,
        globalAnswers,
        updateGlobalAnswer,
        goToNextScreen,
        selectedPlan,
        updateSelectedPlan
    } = useOnboarding();

    const isStripeEnabled = data?.system?.isStripeEnabled !== false;
    const isSolidgateEnabled = data?.system?.isSolidgateEnabled === true;

    const style = applyStyles(element);
    const currentScreen = getCurrentScreen();
    const [selectedPlanIndex, setSelectedPlanIndex] = useState(() => {
        const defaultIndex = element.plans?.findIndex(plan => plan.default === true);
        return defaultIndex !== -1 ? defaultIndex : 0;
    });
    const [descriptionText, setDescriptionText] = useState('');

    const stripeApplePayRef = useRef(null);
    const stripeGooglePayRef = useRef(null);

    const pm = usePaymentStore();

    const [isApplePayAvailable, setIsApplePayAvailable] = useState(pm.isApplePayAvailable);
    const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(pm.isGooglePayAvailable);
    const [isChecking, setIsChecking] = useState(pm.isChecking);
    const [isProcessing, setIsProcessing] = useState(pm.isProcessing);
    const [isSolidgateLoading, setIsSolidgateLoading] = useState(pm.isSolidgateLoading);
    const [isSolidgateApplePayMounted, setIsSolidgateApplePayMounted] = useState(pm.isSolidgateApplePayMounted);
    const [isSolidgateGooglePayMounted, setIsSolidgateGooglePayMounted] = useState(pm.isSolidgateGooglePayMounted);
    const [isSolidgateWalletProcessing, setIsSolidgateWalletProcessing] = useState(pm.isSolidgateWalletProcessing);

    useEffect(() => {
        setIsChecking(pm.isChecking);
        setIsApplePayAvailable(pm.isApplePayAvailable);
        setIsGooglePayAvailable(pm.isGooglePayAvailable);
        setIsProcessing(pm.isProcessing);
        setIsSolidgateLoading(pm.isSolidgateLoading);
        setIsSolidgateApplePayMounted(pm.isSolidgateApplePayMounted);
        setIsSolidgateGooglePayMounted(pm.isSolidgateGooglePayMounted);
        setIsSolidgateWalletProcessing(pm.isSolidgateWalletProcessing);
    }, [pm]);

    // Initialize selected plan from saved data or default
    useEffect(() => {
        if (currentScreen?.name) {
            const savedAnswer = globalAnswers.get(currentScreen.name);
            if (savedAnswer && savedAnswer.planIndex !== undefined) {
                setSelectedPlanIndex(savedAnswer.planIndex);
            } else {
                const defaultIndex = element.plans?.findIndex(plan => plan.default === true);
                if (defaultIndex !== -1 && defaultIndex !== selectedPlanIndex) {
                    setSelectedPlanIndex(defaultIndex);
                    if (element.plans && element.plans[defaultIndex]) {
                        const plan = element.plans[defaultIndex];

                        updateGlobalAnswer(currentScreen.name, {
                            planIndex: defaultIndex,
                            planTitle: plan.title,
                            planPrice: plan.price,
                            planDuration: plan.duration,
                            planFullPrice: plan.fullPrice,
                            elementName: element.name || 'paywall'
                        });
                    }
                }
            }
        }
    }, [currentScreen, globalAnswers, selectedPlanIndex, element, updateGlobalAnswer]);

    // Sync selectedPlan in context with selectedPlanIndex
    useEffect(() => {
        if (element.plans && element.plans[selectedPlanIndex]) {
            const plan = element.plans[selectedPlanIndex];
            const resolved = resolvePlanPrices(plan);

            updateSelectedPlan({
                id: plan.id,
                title: plan.title,
                price: plan.price,
                priceID: resolved.priceID,
                discountID: resolved.discountID,
                oldPrice: plan.oldPrice,
                fullPrice: plan.fullPrice,
                duration: plan.duration,
                period: plan.period,
                paddle: resolved.paddle,
                solidgate: resolved.solidgate,
                pricePerDay: plan.pricePerDay,
                planIndex: selectedPlanIndex
            });
        }
    }, [selectedPlanIndex, element.plans, updateSelectedPlan]);

    // Update description when selected plan changes
    useEffect(() => {
        if (element.plans && element.plans[selectedPlanIndex] && element.descriptionTemplate) {
            const selectedPlan = element.plans[selectedPlanIndex];
            let text = element.descriptionTemplate;

            text = text.replace(/\{\{duration\}\}/g, selectedPlan.duration || '1 month');
            text = text.replace(/\{\{fullPrice\}\}/g, currencySign() + selectedPlan.oldPrice || '$39.99');
            text = text.replace(/\{\{period\}\}/g, selectedPlan.period || '1 month');

            text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a target="_blank" href="$2">$1</a>');

            setDescriptionText(text);
        }
    }, [selectedPlanIndex, element.plans, element.descriptionTemplate]);

    const handlePlanSelect = (index) => {
        const plan = element.plans[index];
        setSelectedPlanIndex(index);

        if (currentScreen?.name) {
            updateGlobalAnswer(currentScreen.name, {
                planIndex: index,
                planTitle: plan.title,
                planPrice: plan.price,
                planDuration: plan.duration,
                planFullPrice: plan.fullPrice,
                elementName: element.name || 'paywall'
            });
        }
    };


    const handleBuyApplePayClick = () => {
        console.log('Buy Apple Pay clicked');
        if (stripeApplePayRef.current) {
            stripeApplePayRef.current.handleApplePayClick();
        }
    };

    const handleBuyGooglePayClick = () => {
        console.log('Buy Google Pay clicked');
        if (stripeGooglePayRef.current) {
            stripeGooglePayRef.current.handleGooglePayClick();
            paymentStore.setState({ isProcessing: true });
        }
    };

    const handleScrollToPaymentClick = () => {
        console.log('Primary button clicked, selected plan:', element.plans[selectedPlanIndex]);

        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
        });

        fbq(
            'track',
            'InitiateCheckout'
        );
    };

    const handleSecondaryClick = () => {
        console.log('Secondary button clicked');
        goToNextScreen();
    };

    if (!element.plans || !Array.isArray(element.plans)) {
        return null;
    }

    const solidgateWalletsReady =
        pm.isSolidgateAppleWalletResolved && pm.isSolidgateGoogleWalletResolved;

    // Priority: Stripe Apple Pay > Stripe Google Pay > (wait for Solidgate) > Solidgate Apple Pay > Solidgate Google Pay > fallback
    let activeButton = 'fallback';
    if (isChecking) {
        activeButton = 'checking';
    } else if (isStripeEnabled && isApplePayAvailable) {
        activeButton = 'stripeApplePay';
    } else if (isStripeEnabled && isGooglePayAvailable) {
        activeButton = 'stripeGooglePay';
    } else if (
        isSolidgateEnabled &&
        (pm.isSolidgateLoading || !solidgateWalletsReady)
    ) {
        activeButton = 'checking';
    } else if (isSolidgateEnabled && pm.isSolidgateApplePayMounted) {
        activeButton = 'solidgateApplePay';
    } else if (isSolidgateEnabled && pm.isSolidgateGooglePayMounted) {
        activeButton = 'solidgateGooglePay';
    }

    return (
        <div className="element-paywall-items" style={style}>
            <div className="paywall-plans-container">
                {element.plans.map((plan, index) => (
                    <div key={index} className="paywall-plan-card">
                        {plan.badge && (
                            <div className="paywall-badge-container">
                                <div className="paywall-badge">{plan.badge}</div>
                            </div>
                        )}
                        <button
                            className={`paywall-plan-button ${index === selectedPlanIndex ? 'selected' : ''}`}
                            onClick={() => handlePlanSelect(index)}
                        >
                            <div className="paywall-plan-body">
                                <div className="paywall-plan-details">
                                    <div className="paywall-plan-title">{plan.title || ''}</div>
                                    <div className="paywall-plan-prices">
                                        {plan.oldPrice && (
                                            <span className="paywall-old-price">{currencySign()} {plan.oldPrice}</span>
                                        )}
                                        <span className="paywall-new-price">{currencySign()} {plan.price ? plan.price : ''}</span>
                                    </div>
                                </div>
                                <div className="paywall-price-block">
                                    <svg
                                        className="paywall-price-shape"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="104"
                                        height="46"
                                        viewBox="0 0 104 46"
                                        fill="none"
                                        preserveAspectRatio="none"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M13.5715 3.25036C15.0796 1.20634 17.4688 0 20.009 0H96C100.418 0 104 3.58172 104 8V38C104 42.4183 100.418 46 96 46H20.009C17.4689 46 15.0796 44.7937 13.5715 42.7496L2.50435 27.7496C0.420961 24.9259 0.420962 21.0741 2.50435 18.2504L13.5715 3.25036Z"
                                        />
                                    </svg>
                                    <div className="paywall-price-content">
                                        <div className="paywall-price-wrap">
                                            <div className="paywall-currency">{currencySign()}</div>
                                            <div className="paywall-int-digits">{plan.pricePerDay?.int || '0'}</div>
                                            <div className="paywall-fraction-container">
                                                <div className="paywall-fraction-digits">{plan.pricePerDay?.fraction || '00'}</div>
                                                <div className="paywall-price-label">{plan.pricePerDay?.label || 'per day'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                ))}
            </div>

            {activeButton === 'checking' && (
                <div className="element-payment-form" style={style}>
                    <div id="payment-form-loader" className="spinner"></div>
                </div>
            )}

            {activeButton === 'stripeApplePay' && (
                <div className="paywall-primary-button-container fade-in-100ms">
                    <button className="paywall-primary-button" onClick={handleBuyApplePayClick}>
                        <div className="applePayButton">
                            {isProcessing ? (
                                <span className="button-spinner">
                                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                                            <animate attributeName="stroke-dasharray" values="1,150;90,150;1,150" dur="1.5s" repeatCount="indefinite" />
                                            <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite" />
                                        </circle>
                                    </svg>
                                </span>
                            ) : (
                                <>
                                    Continue with
                                    <svg className="payment-method-icon svelte-lss1u3" height="26" viewBox="0 0 63 26" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M8.41022 4.82398C9.5916 4.92293 10.773 4.23026 11.5113 3.35205C12.2374 2.4491 12.7173 1.23692 12.5943 0C11.5483 0.0494767 10.2561 0.692674 9.51777 1.59562C8.84093 2.37488 8.26255 3.63654 8.41022 4.82398ZM22.4638 20.7555V1.47193H29.6628C33.3793 1.47193 35.9758 4.04471 35.9758 7.80494C35.9758 11.5652 33.33 14.1627 29.5644 14.1627H25.4418V20.7555H22.4638ZM12.5819 5.05898C11.5411 4.99877 10.5914 5.37358 9.82438 5.67633C9.33075 5.87116 8.91274 6.03614 8.59472 6.03614C8.23784 6.03614 7.80257 5.86234 7.31387 5.66719C6.6735 5.4115 5.94138 5.11916 5.17364 5.13319C3.41387 5.15793 1.77716 6.15983 0.878819 7.75545C-0.967091 10.9467 0.398882 15.6717 2.18326 18.2693C3.05699 19.5556 4.10301 20.9657 5.48129 20.9163C6.08765 20.8933 6.52383 20.7072 6.97524 20.5147C7.49492 20.293 8.0348 20.0628 8.87776 20.0628C9.69151 20.0628 10.2078 20.287 10.7033 20.5023C11.1746 20.707 11.6271 20.9036 12.2989 20.8915C13.7264 20.8668 14.6247 19.6051 15.4984 18.3187C16.4413 16.9381 16.8557 15.5906 16.9186 15.3861C16.9222 15.3745 16.9246 15.3665 16.9259 15.3625C16.9244 15.361 16.9128 15.3556 16.8922 15.3462C16.577 15.2011 14.1679 14.0926 14.1448 11.1199C14.1216 8.62473 16.0556 7.36054 16.3601 7.16153C16.3786 7.14944 16.3911 7.14125 16.3968 7.137C15.1662 5.30636 13.2464 5.10845 12.5819 5.05898ZM41.4153 20.9039C43.2858 20.9039 45.0209 19.9515 45.8085 18.4424H45.8701V20.7555H48.6266V11.157C48.6266 8.37393 46.4115 6.5804 43.0027 6.5804C39.8401 6.5804 37.5019 8.39866 37.4158 10.8972H40.0985C40.32 9.70979 41.4153 8.93054 42.9166 8.93054C44.7379 8.93054 45.7593 9.78401 45.7593 11.3549V12.4186L42.0429 12.6413C38.5849 12.8516 36.7143 14.274 36.7143 16.7479C36.7143 19.2464 38.6464 20.9039 41.4153 20.9039ZM42.215 18.6156C40.6275 18.6156 39.6184 17.8487 39.6184 16.6736C39.6184 15.4615 40.5906 14.7564 42.4488 14.6451L45.7591 14.4348V15.5233C45.7591 17.3292 44.2332 18.6156 42.215 18.6156ZM57.7699 21.51C56.5762 24.8868 55.2103 26 52.306 26C52.0845 26 51.3461 25.9753 51.1739 25.9258V23.6127C51.3585 23.6375 51.8138 23.6622 52.0476 23.6622C53.3643 23.6622 54.1027 23.1056 54.558 21.6584L54.8288 20.8049L49.7833 6.76594H52.8967L56.4039 18.1579H56.4655L59.9727 6.76594H63L57.7699 21.51ZM25.4416 3.99524H28.875C31.4592 3.99524 32.936 5.38059 32.936 7.81731C32.936 10.254 31.4592 11.6518 28.8627 11.6518H25.4416V3.99524Z" fill="currentColor"></path></svg>
                                </>
                            )}
                        </div>
                    </button>
                    <div className="other-payment-button" onClick={handleScrollToPaymentClick}>
                        <h1>Continue with other payment options</h1>
                    </div>
                </div>
            )}

            {activeButton === 'stripeGooglePay' && (
                <div className="paywall-primary-button-container fade-in-100ms">
                    <button className="paywall-primary-button" onClick={handleBuyGooglePayClick}>
                        <div className="applePayButton">
                            {isProcessing ? (
                                <span className="button-spinner">
                                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                                            <animate attributeName="stroke-dasharray" values="1,150;90,150;1,150" dur="1.5s" repeatCount="indefinite" />
                                            <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite" />
                                        </circle>
                                    </svg>
                                </span>
                            ) : (
                                <>
                                    Continue with
                                    <img src="/assets/images/gpay.png" height="30" />
                                    Pay
                                </>
                            )}
                        </div>
                    </button>
                    <div className="other-payment-button" onClick={handleScrollToPaymentClick}>
                        <h1>Continue with other payment options</h1>
                    </div>
                </div>
            )}

            {/* Solidgate containers: always at the same tree position, visibility via CSS */}
            <div style={{ display: activeButton === 'solidgateApplePay' ? 'block' : 'none' }}>
                <div className="paywall-primary-button-container fade-in-100ms">
                    <div style={{ position: 'relative', height: '52px', borderRadius: '10px' }}>
                        <div ref={solidgateApplePayContainerRef} style={{ height: '52px', borderRadius: '10px' }} />
                        {isSolidgateWalletProcessing && activeButton === 'solidgateApplePay' && (
                            <SolidgateWalletSpinnerOverlay  />
                        )}
                    </div>
                    <div className="other-payment-button" onClick={handleScrollToPaymentClick}>
                        <h1>Continue with other payment options</h1>
                    </div>
                </div>
            </div>
            <div style={{ display: activeButton === 'solidgateGooglePay' ? 'block' : 'none' }}>
                <div className="paywall-primary-button-container fade-in-100ms">
                    <div style={{ position: 'relative', height: '52px', borderRadius: '10px' }}>
                        <div ref={solidgateGooglePayContainerRef} style={{ height: '52px', borderRadius: '10px' }} />
                        {isSolidgateWalletProcessing && activeButton === 'solidgateGooglePay' && (
                            <SolidgateWalletSpinnerOverlay />
                        )}
                    </div>
                    <div className="other-payment-button" onClick={handleScrollToPaymentClick}>
                        <h1>Continue with other payment options</h1>
                    </div>
                </div>
            </div>

            {activeButton === 'fallback' && (
                <div className="paywall-primary-button-container fade-in-100ms">
                    <button className="paywall-primary-button" onClick={handleScrollToPaymentClick}>
                        {element.primaryButtonText || 'Get my plan'}
                    </button>
                </div>
            )}

            {descriptionText && (
                <div className="paywall-description-container">
                    <div
                        className="paywall-description"
                        dangerouslySetInnerHTML={{ __html: descriptionText }}
                    />
                </div>
            )}

            {/* Hidden Stripe wallet components for ref access */}
            {isStripeEnabled && (
                <>
                    <div style={{ display: 'none' }}>
                        <StripeApplePay ref={stripeApplePayRef} isExpanded={false} onSelect={() => { }} />
                    </div>
                    <div style={{ display: 'none' }}>
                        <StripeGooglePay ref={stripeGooglePayRef} isExpanded={false} onSelect={() => { }} />
                    </div>
                </>
            )}

        </div>
    );
};

export default PaywallItemsElement;
